import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener('pointerdown', this.#handleVariantPointerDown, { signal, capture: true });
    target?.addEventListener('change', this.#handleVariantPickerChange, { signal, capture: true });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#applyInitialVariantFilter();
    this.#updateSoldOutMessage(this.#getInitialVariant());
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by filtering visible media for the selected variant.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const picker = this.closest('.shopify-section, dialog')?.querySelector('variant-picker');
    const variant = event.detail.resource;

    if (variant) {
      this.#updateSoldOutMessage(variant);
    } else if (picker && this.#isSelectionUnavailable(picker)) {
      this.#updateSoldOutMessage({ available: false });
    } else {
      this.#updateSoldOutMessage(null);
    }

    if (this.dataset.hideVariants === 'true') {
      this.#applyVariantFilter(variant);
      return;
    }

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    requestAnimationFrame(() => {
      const featuredMediaId = this.#getFeaturedMediaId(variant);

      if (featuredMediaId && newMediaGallery.slideshow) {
        newMediaGallery.slideshow.select({ id: featuredMediaId }, undefined, { animate: false });
      } else {
        newMediaGallery.slideshow?.select(0, undefined, { animate: false });
      }
    });
  };

  /**
   * Filters gallery slides and thumbnails to match the live site variant image grouping behavior.
   *
   * @param {object | null | undefined} variant - Selected variant from the variant picker JSON.
   * @param {{ optionValue?: string | null, mediaId?: string | number | null }} [overrides]
   */
  #applyVariantFilter(variant, overrides = {}) {
    if (this.dataset.hideVariants !== 'true') return;

    const targetColor = overrides.optionValue || this.#resolveTargetColor(variant);
    if (!targetColor) return;

    const colorToken = `|${targetColor}|`;
    const featuredMediaId =
      overrides.mediaId ?? this.#getFeaturedMediaId(variant) ?? this.#findMediaIdForOptionValue(targetColor);

    this.#setMediaVisibility(this.querySelectorAll('slideshow-slide[slide-id]'), colorToken, featuredMediaId);
    this.#setMediaVisibility(this.querySelectorAll('.media-gallery__grid [data-media-id]'), colorToken, featuredMediaId);
    this.#setMediaVisibility(
      this.querySelectorAll('.slideshow-controls__thumbnail[data-media-id]'),
      colorToken,
      featuredMediaId
    );
    this.#setMediaVisibility(
      this.querySelectorAll('.dialog-thumbnails-list__thumbnail[data-media-id]'),
      colorToken,
      featuredMediaId
    );

    this.#syncSlideshowToVariant(variant, targetColor, featuredMediaId);
  }

  /**
   * Re-syncs slideshow navigation and thumbnails after variant media visibility changes.
   *
   * @param {object | null | undefined} variant
   * @param {string} targetColor
   * @param {string | number | null | undefined} [preferredMediaId]
   */
  #syncSlideshowToVariant(variant, targetColor, preferredMediaId) {
    const slideshow = this.slideshow;
    if (!slideshow) return;

    const featuredMediaId = preferredMediaId ?? this.#getFeaturedMediaId(variant) ?? this.#findMediaIdForOptionValue(targetColor);
    let targetSlide = null;

    if (featuredMediaId != null) {
      targetSlide = this.querySelector(`slideshow-slide[slide-id="${featuredMediaId}"]`);
    }

    if (!targetSlide) {
      targetSlide = this.#getFirstSlideForColor(targetColor);
    }

    if (!targetSlide) {
      targetSlide = this.querySelector('slideshow-slide[slide-id]:not([hidden])');
    }

    if (!targetSlide) return;

    // Slideshow refuses to select hidden variant-filtered slides — reveal first.
    targetSlide.removeAttribute('hidden');
    targetSlide.setAttribute('aria-hidden', 'false');

    const slideId = targetSlide.getAttribute('slide-id');
    if (!slideId) return;

    slideshow.select({ id: slideId }, undefined, { animate: false });

    const visibleCount = this.querySelectorAll('slideshow-slide[slide-id]:not([hidden])').length;
    this.#updateGalleryControls(visibleCount);
  }

  /**
   * Hides thumbnail controls and carousel arrows when the filtered gallery has only one image.
   *
   * @param {number} visibleCount
   */
  #updateGalleryControls(visibleCount) {
    const slideshow = this.slideshow;
    if (!slideshow) return;

    const { slideshowControls, previous, next } = slideshow.refs;

    if (slideshowControls instanceof HTMLElement) {
      slideshowControls.hidden = visibleCount <= 1;
    }

    const arrows = this.querySelector('slideshow-arrows');
    if (arrows instanceof HTMLElement) {
      arrows.hidden = visibleCount <= 1;
    }

    // Theme CSS hides arrows when slideshow[disabled=true]
    if (visibleCount <= 1) {
      slideshow.setAttribute('disabled', 'true');
      if (previous) previous.disabled = true;
      if (next) next.disabled = true;
    } else {
      slideshow.removeAttribute('disabled');
      // Refresh prev/next disabled state for the active slide index
      slideshow.current = slideshow.current;
    }
  }

  /**
   * @param {string} targetColor
   * @returns {Element | null}
   */
  #getFirstSlideForColor(targetColor) {
    const colorToken = `|${targetColor}|`;

    for (const slide of this.querySelectorAll('slideshow-slide[slide-id]')) {
      const variantColors = slide.getAttribute('data-variant-colors');
      if (variantColors && `|${variantColors}|`.includes(colorToken)) {
        return slide;
      }
    }

    return null;
  }

  /**
   * @param {NodeListOf<Element> | Element[]} elements
   * @param {string} colorToken
   * @param {string | number | null | undefined} [featuredMediaId]
   */
  #setMediaVisibility(elements, colorToken, featuredMediaId) {
    const featuredId = featuredMediaId != null ? String(featuredMediaId) : null;

    elements.forEach((element) => {
      const variantColors = element.getAttribute('data-variant-colors');
      const mediaId =
        element.getAttribute('slide-id') || element.getAttribute('data-media-id') || element.dataset?.mediaId;
      const matchesColor = Boolean(variantColors && `|${variantColors}|`.includes(colorToken));
      const matchesFeatured = Boolean(featuredId && mediaId && String(mediaId) === featuredId);
      const showMedia = matchesColor || matchesFeatured;

      element.toggleAttribute('hidden', !showMedia);
      element.setAttribute('aria-hidden', String(!showMedia));
    });
  }

  /**
   * @param {object | null | undefined} variant
   * @returns {string | number | null}
   */
  #getFeaturedMediaId(variant) {
    return variant?.featured_media?.id ?? variant?.featured_image?.id ?? null;
  }

  /**
   * Finds a featured media id for an option value when the exact size/color combo has no image.
   *
   * @param {string} optionValue
   * @returns {string | number | null}
   */
  #findMediaIdForOptionValue(optionValue) {
    const colorOptionIndex = this.dataset.colorOptionIndex;
    if (colorOptionIndex === undefined || !optionValue) return null;

    const index = Number.parseInt(colorOptionIndex, 10);
    const optionKey = `option${index + 1}`;
    const variants = this.#getVariantsList();
    if (!variants?.length) return null;

    const match = variants.find((variant) => {
      const value = variant[optionKey] ?? variant.options?.[index];
      return value === optionValue && (variant.featured_media?.id || variant.featured_image?.id);
    });

    return match ? this.#getFeaturedMediaId(match) : null;
  }

  /**
   * @param {object | null | undefined} variant
   * @returns {string | null}
   */
  #resolveTargetColor(variant) {
    return this.#getVariantColor(variant) ?? this.#getSelectedColorFromPicker();
  }

  /**
   * @param {object | null | undefined} variant
   * @returns {string | null}
   */
  #getVariantColor(variant) {
    if (!variant) return null;

    const colorOptionIndex = this.dataset.colorOptionIndex;

    if (colorOptionIndex === undefined) return null;

    const index = Number.parseInt(colorOptionIndex, 10);
    const optionKey = `option${index + 1}`;

    return variant[optionKey] ?? variant.options?.[index] ?? null;
  }

  /**
   * Reads the selected color option from the variant picker (used when no valid variant exists, e.g. Denim + size 6).
   *
   * @returns {string | null}
   */
  #getSelectedColorFromPicker() {
    const colorOptionIndex = this.dataset.colorOptionIndex;
    if (colorOptionIndex === undefined) return null;

    const picker = this.closest('.shopify-section, dialog')?.querySelector('variant-picker');
    if (!picker) return null;

    const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
    const colorFieldset = fieldsets[Number.parseInt(colorOptionIndex, 10)];
    if (!colorFieldset) return null;

    const checkedInput = colorFieldset.querySelector('input[type="radio"]:checked');
    if (checkedInput instanceof HTMLInputElement) {
      // Prefer the option value — aria-labels like "Emma Slide Sandal - Black" must stay intact.
      if (checkedInput.value) return checkedInput.value;

      const label = checkedInput.getAttribute('aria-label');
      if (label) return label.trim();
    }

    const select = colorFieldset.querySelector('select');
    if (select instanceof HTMLSelectElement) {
      return select.value || select.options[select.selectedIndex]?.textContent?.trim() || null;
    }

    return null;
  }

  /** @type {object[] | null} */
  #variantsList = null;

  /**
   * @returns {object[] | null}
   */
  #getVariantsList() {
    if (this.#variantsList) return this.#variantsList;

    const script = this.querySelector('script[data-product-variants]');
    if (!script?.textContent) return null;

    try {
      this.#variantsList = JSON.parse(script.textContent);
    } catch {
      this.#variantsList = null;
    }

    return this.#variantsList;
  }

  /**
   * Resolves the selected variant client-side (matches Live theme — no network wait).
   *
   * @param {Element} picker
   * @param {HTMLInputElement} [clickedInput] - Option being selected (may not be checked yet on pointerdown).
   * @returns {object | null}
   */
  #resolveVariantFromPicker(picker, clickedInput) {
    const variants = this.#getVariantsList();
    if (!variants?.length) return null;

    const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
    if (!fieldsets.length) return null;

    /** @type {(string | null)[]} */
    const selectedOptions = fieldsets.map((fieldset) => {
      if (clickedInput instanceof HTMLInputElement && clickedInput.closest('fieldset') === fieldset) {
        return clickedInput.value;
      }

      const checked = fieldset.querySelector('input[type="radio"]:checked');
      return checked instanceof HTMLInputElement ? checked.value : null;
    });

    if (selectedOptions.some((option) => option == null)) return null;

    return (
      variants.find((variant) =>
        selectedOptions.every((option, index) => variant.options?.[index] === option)
      ) ?? null
    );
  }

  /**
   * @param {object | null | undefined} variant
   */
  #updateSoldOutMessage(variant) {
    const message = this.querySelector('[data-sold-out-message]');
    // Discontinued products / variants never get the arrival-notification prompt
    const discontinuedIds = (message?.getAttribute('data-discontinued-variants') || '').split(',');
    const notifyExcluded =
      message?.getAttribute('data-notify-excluded') === 'true' ||
      Boolean(variant?.id && discontinuedIds.includes(String(variant.id)));
    const show = Boolean(variant && variant.available === false && !notifyExcluded);

    if (message) {
      message.hidden = !show;
    }

    this.dispatchEvent(
      new CustomEvent('sold-out-notify:update', {
        bubbles: true,
        detail: { show, variant: variant?.id ? variant : null },
      })
    );
  }

  /**
   * Shows or hides the sold-out alert immediately on option press (before fetch).
   * Media sync stays on `change` — updating the gallery during pointerdown can cancel
   * the tap on mobile (first tap updates media, second tap selects the swatch).
   *
   * @param {PointerEvent} event
   */
  #handleVariantPointerDown = (event) => {
    if (!this.querySelector('[data-sold-out-message]')) return;

    const input = this.#getVariantInputFromEvent(event);
    if (!input) return;

    const picker = input.closest('variant-picker');
    const section = this.closest('.shopify-section, dialog');
    if (!picker || !section?.contains(picker)) return;

    this.#syncSoldOutMessageFromPicker(picker, input);
  };

  /**
   * @param {Event} event
   */
  #handleVariantPickerChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.checked) return;

    const picker = event.target.closest('variant-picker');
    const section = this.closest('.shopify-section, dialog');
    if (!picker || !section?.contains(picker)) return;

    if (this.querySelector('[data-sold-out-message]')) {
      this.#syncSoldOutMessageFromPicker(picker, event.target);
    }

    this.#syncMediaFromPicker(picker, event.target);
  };

  /**
   * Updates gallery media as soon as a grouping option is chosen (before section fetch).
   *
   * @param {Element} picker
   * @param {HTMLInputElement} input
   */
  #syncMediaFromPicker(picker, input) {
    if (this.dataset.hideVariants !== 'true') return;

    const colorOptionIndex = this.dataset.colorOptionIndex;
    if (colorOptionIndex === undefined) return;

    const fieldsetIndex = Number.parseInt(input.dataset.fieldsetIndex || '', 10);
    const groupingIndex = Number.parseInt(colorOptionIndex, 10);
    const optionValue = input.value || null;
    const mediaId = input.dataset.mediaId || null;

    // Only eager-update when the clicked option is the media grouping option (e.g. Style Name & Color).
    if (!Number.isNaN(fieldsetIndex) && fieldsetIndex !== groupingIndex && !mediaId) {
      const variant = this.#resolveVariantFromPicker(picker, input);
      this.#applyVariantFilter(variant);
      return;
    }

    const variant = this.#resolveVariantFromPicker(picker, input);
    this.#applyVariantFilter(variant, {
      // Prefer the clicked option value whenever this input owns media (style/color buttons).
      optionValue: fieldsetIndex === groupingIndex || mediaId ? optionValue : undefined,
      mediaId,
    });
  }

  /**
   * @param {Element} picker
   * @param {HTMLInputElement} input
   */
  #syncSoldOutMessageFromPicker(picker, input) {
    const variant = this.#resolveVariantFromPicker(picker, input);

    if (variant) {
      this.#updateSoldOutMessage(variant);
      return;
    }

    if (this.#isSelectionUnavailable(picker, input)) {
      this.#updateSoldOutMessage({ available: false });
      return;
    }

    const fallbackVariant = this.#resolveVariantFromInputAttributes(input);
    this.#updateSoldOutMessage(fallbackVariant);
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {boolean}
   */
  #isOptionInputUnavailable(input) {
    if (!(input instanceof HTMLInputElement)) return false;
    if (input.getAttribute('aria-disabled') === 'true') return true;

    const optionAvailable = input.getAttribute('data-option-available');
    return optionAvailable !== null && optionAvailable !== 'true';
  }

  /**
   * True when any selected (or clicked) option is unavailable — includes sizes that do not exist for a color.
   *
   * @param {Element} picker
   * @param {HTMLInputElement} [clickedInput]
   * @returns {boolean}
   */
  #isSelectionUnavailable(picker, clickedInput) {
    const fieldsets = Array.from(picker.querySelectorAll('fieldset'));

    for (const fieldset of fieldsets) {
      /** @type {HTMLInputElement | null} */
      let input = null;

      if (clickedInput instanceof HTMLInputElement && clickedInput.closest('fieldset') === fieldset) {
        input = clickedInput;
      } else {
        input = fieldset.querySelector('input[type="radio"]:checked');
      }

      if (this.#isOptionInputUnavailable(input)) return true;
    }

    return false;
  }

  /**
   * @param {Event} event
   * @returns {HTMLInputElement | null}
   */
  #getVariantInputFromEvent(event) {
    if (!(event.target instanceof Element)) return null;

    const label = event.target.closest('.variant-option__button-label');
    const input = label?.querySelector('input[type="radio"]');

    if (input instanceof HTMLInputElement) return input;
    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') return event.target;

    return null;
  }

  /**
   * Fallback when variants JSON is unavailable — uses picker input attributes.
   *
   * @param {HTMLInputElement} input
   * @returns {object | null}
   */
  #resolveVariantFromInputAttributes(input) {
    const variantId = input.dataset.variantId;
    if (variantId) {
      const variant = this.#getVariantsList()?.find((v) => String(v.id) === String(variantId));
      if (variant) return variant;
    }

    if (this.#isOptionInputUnavailable(input)) {
      return { available: false };
    }

    if (input.getAttribute('data-option-available') === 'true') {
      return { available: true };
    }

    return null;
  }

  /**
   * Applies variant media filtering on load (URL ?variant= or variant picker JSON).
   */
  #applyInitialVariantFilter() {
    const variant = this.#getInitialVariant();

    const apply = () => this.#applyVariantFilter(variant);

    const runAfterSlideshowReady = () => {
      requestAnimationFrame(() => {
        apply();
        requestAnimationFrame(apply);
      });
    };

    if (this.slideshow) {
      runAfterSlideshowReady();
    } else {
      customElements.whenDefined('slideshow-component').then(runAfterSlideshowReady);
    }
  }

  /**
   * @returns {object | null}
   */
  #getInitialVariant() {
    return this.#getVariantFromUrl() ?? this.#getVariantFromPicker();
  }

  /**
   * @returns {object | null}
   */
  #getVariantFromUrl() {
    const variantId = new URLSearchParams(window.location.search).get('variant');
    if (!variantId) return null;

    const variantsScript = this.querySelector('script[data-product-variants]');
    if (!variantsScript?.textContent) return null;

    try {
      const variants = JSON.parse(variantsScript.textContent);
      return variants.find((variant) => String(variant.id) === String(variantId)) ?? null;
    } catch {
      return null;
    }
  }

  /**
   * @returns {object | null}
   */
  #getVariantFromPicker() {
    const variantScript = this.closest('.shopify-section, dialog')?.querySelector(
      'variant-picker script[type="application/json"]'
    );

    if (!variantScript?.textContent) return null;

    try {
      return JSON.parse(variantScript.textContent);
    } catch {
      return null;
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to preload.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
